import React, { useState, useEffect, useContext } from 'react';
import {
	Box,
	Card,
	CardContent,
	Typography,
	TextField,
	Button,
	Alert,
	CircularProgress,
	Container,
	Stepper,
	Step,
	StepLabel,
	Link,
} from '@mui/material';
import { useParams, useNavigate } from 'react-router-dom';
import SnackbarContext from '../store/snackbar-context';
import { verifyEmailClick, verifyPassword, verifyCode } from '../api/email-campaign-api';
import { isSmallOlympicVariant } from '../config/variant-profile';

const SUPPORT_EMAIL = isSmallOlympicVariant() ? 'olijpSP@ibl.waw.pl' : 'olijp@ibl.waw.pl';

interface VerificationRequirements {
	require_link: boolean;
	require_password: boolean;
	require_code: boolean;
	campaign_name: string;
	recipient_name: string;
	recipient_email_masked: string;
	link_clicked: boolean;
	password_verified: boolean;
	code_verified: boolean;
}

const EmailVerification: React.FC = () => {
	const { token } = useParams<{ token: string }>();
	const navigate = useNavigate();
	const { setMsg } = useContext(SnackbarContext);

	const [requirements, setRequirements] = useState<VerificationRequirements | null>(null);
	const [currentStep, setCurrentStep] = useState(0);
	const [isLoading, setIsLoading] = useState(true);
	const [password, setPassword] = useState('');
	const [code, setCode] = useState('');
	const [isVerifying, setIsVerifying] = useState(false);
	const [verificationComplete, setVerificationComplete] = useState(false);
	const [error, setError] = useState<string | null>(null);

	useEffect(() => {
		if (token) {
			loadVerificationRequirements();
		}
	}, [token]);

	useEffect(() => {
		if (requirements) {
			calculateCurrentStep();
		}
	}, [requirements]);

	const loadVerificationRequirements = async () => {
		if (!token) return;

		try {
			setIsLoading(true);
			const response = await verifyEmailClick(token);

			if (response.status === 200) {
				setRequirements(response.data.requirements);
				setError(null);
			} else {
				setError(response.data?.message || 'Nieprawidłowy token weryfikacyjny');
			}
		} catch (exception: any) {
			console.error(exception);
			setError('Błąd podczas weryfikacji. Sprawdź czy link jest prawidłowy.');
		} finally {
			setIsLoading(false);
		}
	};

	const calculateCurrentStep = () => {
		if (!requirements) return;

		let step = 0;

		// Step 1: Link click (if required)
		if (requirements.require_link && !requirements.link_clicked) {
			setCurrentStep(0);
			return;
		}
		step++;

		// Step 2: Password verification (if required)
		if (requirements.require_password && !requirements.password_verified) {
			setCurrentStep(step);
			return;
		}
		step++;

		// Step 3: Code verification (if required)
		if (requirements.require_code && !requirements.code_verified) {
			setCurrentStep(step);
			return;
		}

		// All steps completed
		setCurrentStep(step);
		setVerificationComplete(true);
	};

	const handlePasswordVerification = async () => {
		if (!token || !password.trim()) return;

		try {
			setIsVerifying(true);
			const response = await verifyPassword(token, password);

			if (response.status === 200) {
				setMsg({ msg: 'Hasło zostało zweryfikowane pomyślnie' });
				setPassword('');
				await loadVerificationRequirements(); // Reload requirements
			} else {
				setError(response.data?.message || 'Nieprawidłowe hasło');
			}
		} catch (exception: any) {
			console.error(exception);
			setError('Błąd podczas weryfikacji hasła');
		} finally {
			setIsVerifying(false);
		}
	};

	const handleCodeVerification = async () => {
		if (!token || !code.trim()) return;

		try {
			setIsVerifying(true);
			const response = await verifyCode(token, code);

			if (response.status === 200) {
				if (response.data.is_valid) {
					setMsg({ msg: 'Kod został zweryfikowany pomyślnie!' });
				} else {
					setError('Nieprawidłowy kod. Spróbuj ponownie.');
				}
				setCode('');
				await loadVerificationRequirements(); // Reload requirements
			} else {
				setError(response.data?.message || 'Błąd podczas weryfikacji kodu');
			}
		} catch (exception: any) {
			console.error(exception);
			setError('Błąd podczas weryfikacji kodu');
		} finally {
			setIsVerifying(false);
		}
	};

	const getStepLabels = () => {
		if (!requirements) return [];

		const labels = [];

		if (requirements.require_link) {
			labels.push('Kliknięcie w link');
		}

		if (requirements.require_password) {
			labels.push('Weryfikacja hasłem');
		}

		if (requirements.require_code) {
			labels.push('Weryfikacja kodem');
		}

		return labels;
	};

	const renderCurrentStep = () => {
		if (!requirements) return null;

		// Step 1: Link click (if required)
		if (requirements.require_link && !requirements.link_clicked) {
			return (
				<Box textAlign='center'>
					<Typography
						variant='h6'
						gutterBottom
					>
						Potwierdzenie otrzymania wiadomości
					</Typography>
					<Typography
						variant='body1'
						color='text.secondary'
						paragraph
					>
						Kliknięcie w link zostało zarejestrowane automatycznie.
					</Typography>
					<Button
						variant='contained'
						onClick={loadVerificationRequirements}
						disabled={isVerifying}
					>
						Kontynuuj
					</Button>
				</Box>
			);
		}

		// Step 2: Password verification (if required)
		if (requirements.require_password && !requirements.password_verified) {
			return (
				<Box>
					<Typography
						variant='h6'
						gutterBottom
					>
						Weryfikacja hasłem
					</Typography>
					<Typography
						variant='body1'
						color='text.secondary'
						paragraph
					>
						Wprowadź swoje hasło, aby potwierdzić tożsamość.
					</Typography>
					<TextField
						label='Hasło'
						type='password'
						value={password}
						onChange={(e) => setPassword(e.target.value)}
						fullWidth
						margin='normal'
						onKeyPress={(e) => {
							if (e.key === 'Enter') {
								handlePasswordVerification();
							}
						}}
					/>
					<Box mt={2}>
						<Button
							variant='contained'
							onClick={handlePasswordVerification}
							disabled={isVerifying || !password.trim()}
							startIcon={isVerifying ? <CircularProgress size={20} /> : undefined}
						>
							{isVerifying ? 'Weryfikacja...' : 'Potwierdź hasło'}
						</Button>
					</Box>
				</Box>
			);
		}

		// Step 3: Code verification (if required)
		if (requirements.require_code && !requirements.code_verified) {
			return (
				<Box>
					<Typography
						variant='h6'
						gutterBottom
					>
						Weryfikacja kodem z PDF
					</Typography>
					<Typography
						variant='body1'
						color='text.secondary'
						paragraph
					>
						Wpisz 4-cyfrowy kod z załączonego PDF, aby potwierdzić, że dokument jest czytelny.
					</Typography>
					<TextField
						label='Kod weryfikacyjny'
						value={code}
						onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 4))}
						fullWidth
						margin='normal'
						inputProps={{ maxLength: 4 }}
						placeholder='1234'
						onKeyPress={(e) => {
							if (e.key === 'Enter') {
								handleCodeVerification();
							}
						}}
					/>
					<Box mt={2}>
						<Button
							variant='contained'
							onClick={handleCodeVerification}
							disabled={isVerifying || code.length !== 4}
							startIcon={isVerifying ? <CircularProgress size={20} /> : undefined}
						>
							{isVerifying ? 'Weryfikacja...' : 'Potwierdź kod'}
						</Button>
					</Box>
				</Box>
			);
		}

		// All steps completed
		return (
			<Box textAlign='center'>
				<Typography
					variant='h4'
					gutterBottom
					color='success.main'
					sx={{ fontWeight: 'bold', mb: 3 }}
				>
					✅ Weryfikacja zakończona pomyślnie!
				</Typography>
				<Typography
					variant='body1'
					color='text.secondary'
					paragraph
				>
					Dziękujemy za potwierdzenie otrzymania wysyłki na adres:{' '}
					<strong>{requirements.recipient_email_masked}</strong>
				</Typography>
				<Typography
					variant='body2'
					color='text.secondary'
					sx={{ mb: 3 }}
				>
					Potwierdzenie zostało zarejestrowane w systemie.
				</Typography>
				<Box
					display='flex'
					gap={2}
					justifyContent='center'
				>
					<Button
						variant='contained'
						onClick={() => navigate('/')}
					>
						Przejdź do strony głównej
					</Button>
					<Button
						variant='outlined'
						onClick={() => navigate('/login')}
					>
						Zaloguj się
					</Button>
				</Box>
			</Box>
		);
	};

	if (isLoading) {
		return (
			<Container
				maxWidth='sm'
				sx={{ mt: 4 }}
			>
				<Box
					display='flex'
					justifyContent='center'
					alignItems='center'
					minHeight='400px'
				>
					<CircularProgress />
				</Box>
			</Container>
		);
	}

	if (error) {
		return (
			<Container
				maxWidth='sm'
				sx={{ mt: 4 }}
			>
				<Card>
					<CardContent>
						<Alert
							severity='error'
							sx={{ mb: 2 }}
						>
							{error}
						</Alert>
						<Typography
							variant='body2'
							color='text.secondary'
						>
							Jeśli problem się powtarza, skontaktuj się z organizatorami olimpiady.
						</Typography>
						<Box mt={2}>
							<Button
								variant='outlined'
								onClick={() => navigate('/')}
							>
								Przejdź do strony głównej
							</Button>
						</Box>
					</CardContent>
				</Card>
			</Container>
		);
	}

	if (!requirements) {
		return (
			<Container
				maxWidth='sm'
				sx={{ mt: 4 }}
			>
				<Card>
					<CardContent>
						<Alert severity='warning'>Nie można załadować informacji o weryfikacji.</Alert>
					</CardContent>
				</Card>
			</Container>
		);
	}

	return (
		<Container
			maxWidth='sm'
			sx={{ mt: 4 }}
		>
			<Card>
				<CardContent>
					{/* Header */}
					<Box
						textAlign='center'
						mb={3}
					>
						<Typography
							variant='h5'
							gutterBottom
						>
							Potwierdzenie otrzymania wiadomości
						</Typography>
					</Box>

					{/* Progress Stepper */}
					{getStepLabels().length > 1 && (
						<Box mb={3}>
							<Stepper
								activeStep={currentStep}
								alternativeLabel
							>
								{getStepLabels().map((label) => (
									<Step key={label}>
										<StepLabel>{label}</StepLabel>
									</Step>
								))}
							</Stepper>
						</Box>
					)}

					{/* Current Step Content */}
					{renderCurrentStep()}

					{/* Footer */}
					<Box
						mt={4}
						textAlign='center'
					>
						<Typography
							variant='body2'
							color='text.secondary'
						>
							Kontakt e-mail z Biurem Olimpiady: <Link href={`mailto:${SUPPORT_EMAIL}`}>{SUPPORT_EMAIL}</Link>
						</Typography>
					</Box>
				</CardContent>
			</Card>
		</Container>
	);
};

export default EmailVerification;
